import React, { createContext, useContext, useState, useEffect } from 'react';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({
    designAgent: false,
    integrationAgent: false,
    migrationAgent: false,       // umbrella — grants both migration types
    migrationDotnet: false,      // MuleSoft → .NET migration only
    migrationJitterbit: false,   // Jitterbit → MuleSoft migration only
    upgradeAgent: false,
    projectCenter: false,
    templates: false,
    connectedApps: false,
    apiTemplates: false,
    smartHub: false,
    greenfieldAgent: false,
    brownfieldAgent: false,
    tenantAdmin: false,
    superAdmin: false,
    techMule: false,
    techSpringBoot: false,
    techDotNet: false,
  });

  const [user, setUser] = useState(null);

  useEffect(() => {
    // Load permissions from localStorage on mount
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      // Derive role-based access from JWT claims: super admins get `superAdmin`,
      // tenant admins get `tenantAdmin` even if their pagePermissions don't set it.
      const derived = {
        ...(userData.pagePermissions || {}),
        superAdmin: userData.platformRole === 'superadmin',
        tenantAdmin: (userData.pagePermissions && userData.pagePermissions.tenantAdmin) || userData.tenantRole === 'admin' || userData.platformRole === 'superadmin',
      };
      setPermissions(prev => ({ ...prev, ...derived }));
    }
  }, []);

  const updatePermissions = (newPermissions) => {
    setPermissions(newPermissions);
    
    // Update localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      userData.pagePermissions = newPermissions;
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const hasPermission = (page) => {
    const val = permissions[page];
    return val === true || val === "true";
  };

  const logout = () => {
    setPermissions({
      designAgent: false,
      integrationAgent: false,
      migrationAgent: false,
      migrationDotnet: false,
      migrationJitterbit: false,
      upgradeAgent: false,
      projectCenter: false,
      templates: false,
      connectedApps: false,
      apiTemplates: false,
      smartHub: false,
      greenfieldAgent: false,
      brownfieldAgent: false,
      tenantAdmin: false,
      techMule: false,
      techSpringBoot: false,
      techDotNet: false,
    });
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  return (
    <PermissionContext.Provider 
      value={{ 
        permissions, 
        updatePermissions, 
        hasPermission,
        user,
        logout
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};